/**
 * One description for an action's failure (TMMRSL-0055). Runs `useApiErrorHandler`'s side
 * effects, then resolves a translated message. The server's English message is never read.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError, backendCode } from '@/api/errors';
import { fieldErrorsOf } from '@/api/fieldErrors';
import { describeApiError, useApiErrorHandler } from './useApiErrorHandler';

export interface ActionError {
  /** Translated; never the server's text. */
  message: string;
  /** Server field keys (as sent), for `mapFieldErrors`. */
  fieldKeys: string[];
  /** The backend code (`details.code`), if any, for callers that branch on it. */
  code: string | undefined;
  /** `ApiError.requestId`, shown as "Reference: {id}" by `ActionErrorAlert` (TMM-B3). */
  requestId: string | undefined;
}

/** A feature's own codes. Return `null` to fall through. */
export type FeatureMap = (err: unknown, t: TFunction) => string | null;

/**
 * Describe an action's failure. No side effects — safe outside a component.
 * Resolution: the feature map, then `errors.checkFields` for a validation that names fields,
 * then `describeApiError`.
 */
export function describeAction(
  err: unknown,
  t: TFunction,
  featureMap?: FeatureMap,
): ActionError {
  const fieldKeys = Object.keys(fieldErrorsOf(err));
  const code = backendCode(err);
  const mapped = featureMap?.(err, t) ?? null;
  let message: string;
  if (mapped !== null && mapped.length > 0) {
    message = mapped;
  } else if (err instanceof ApiError && err.code === 'VALIDATION' && fieldKeys.length > 0) {
    message = t('errors.checkFields');
  } else {
    message = describeApiError(err, t).message;
  }
  return { message, fieldKeys, code, requestId: err instanceof ApiError ? err.requestId : undefined };
}

/**
 * Describe an action's failure. Runs `useApiErrorHandler`'s side effects (revoked, role-forbidden),
 * then `describeAction`. The server's English message is never read.
 */
export function useActionError(featureMap?: FeatureMap): (err: unknown) => ActionError {
  const handle = useApiErrorHandler();
  const { t } = useTranslation();
  return useCallback(
    (err: unknown) => {
      handle(err);
      return describeAction(err, t, featureMap);
    },
    [handle, t, featureMap],
  );
}
